import React from 'react';
import PropTypes from 'prop-types';
import {useRouter} from 'next/router';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowLeft} from '@fortawesome/free-solid-svg-icons';

import Link from '../../components/Link';
import Pager from '../../components/Pager';
import DismissibleCallout from '../../components/DismissibleCallout';
import QuestionListPlaceholder from './QuestionListPlaceholder';
import ListItem from './ListItem';
import PromotionalListItem from './PromotionalListItem';
import {useAnalytics} from '../../hooks';
import {createURLWithParams} from '../../util';

const getPaginationInfo = (questions) => {
  const {results, pagination} = questions;
  if (pagination.count === 0) return '';
  const offset = (pagination.current - 1) * pagination.limit;
  return `Showing ${1 + offset}-${results.length + offset} of ${pagination.count}`;
};

const getPaginationURL = ({
  company, role, type, query, stage, sortBy, filterBy,
}) => createURLWithParams('/questions', {
  company, role, type, query, stage, sortBy, filterBy,
});

const QuestionList = ({
  questions,
  isGated,
  isLoading,
  onSave,
  onClickPaywalledContent,
  query,
  showPromo,
  tagMap,
}) => {
  const {tracker} = useAnalytics();
  const router = useRouter();
  const {
    company, role, type, filterBy,
  } = query;

  const onClickQuestion = (e, question) => {
    if (isGated) {
      e.preventDefault();
      onClickPaywalledContent(question.id);
    } else if (e.metaKey || e.ctrlKey) { // mimic CTRL+CLICK behavior
      window.open(`/questions/${question.id}/${question.slug}`, '_blank');
    } else {
      router.push(`/questions/${question.id}/${question.slug}`);
    }

    tracker.questionListClickQuestion({
      id: question.id,
      question: question.id,
      video: !!question.data.video,
    });
  };

  if (isLoading) return <QuestionListPlaceholder animated height={6} />;

  if (!questions.results.length) {
    return (
      <div className="">
        <section className="">
          <div className="py-6 mx-auto text-center">
            <div className="font-medium text-gray-600">
              {filterBy === 'saved' && !company && !role && !type ? (
                <p>No questions saved yet!</p>
              ) : (
                <p>Sorry, no results found.</p>
              )}
              <Link href="/questions" className="text-indigo">
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" size="md" />
                Go back to all questions
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const questionToListItem = (question) => (
    <ListItem
      onSave={onSave}
      isGated={isGated}
      question={question}
      onClick={(e) => onClickQuestion(e, question)}
      key={question.id}
      query={query}
      tagMap={tagMap}
    />
  );

  const promoPosition = 5;
  const questionListStart = questions.results.slice(0, promoPosition).map(questionToListItem);
  const questionListEnd = questions.results.slice(promoPosition).map(questionToListItem);
  const paginationInfo = getPaginationInfo(questions);

  return (
    <>
      <ul>
        {questionListStart}
        {showPromo ? (
          <PromotionalListItem
            title="Get your questions answered"
            subtitle="Our question database is made possible by user contributions. What were you asked in your most recent interview?"
            btnText="Share"
            href="/questions/contribute"
            onClick={() => tracker.questionListClickContribute({button: 'list'})}
          />
        ) : (
          <DismissibleCallout className="mb-4" id="save-to-vote-question">
            <p>
              <span className="font-medium">🧠 Want an expert answer to a question? </span>
              Saving questions lets us know what content to make next.
            </p>
          </DismissibleCallout>
        )}
        {questionListEnd}
      </ul>
      <div className="flex flex-col-reverse md:flex-row justify-center md:justify-between items-center">
        <span className="mb-5 md:m-0 font-medium text-gray-600">{paginationInfo}</span>
        <div className="my-5" onClickCapture={onClickPaywalledContent}>
          <Pager pagination={questions.pagination} href={getPaginationURL(query)} />
        </div>
      </div>
    </>
  );
};

const QuestionShape = PropTypes.shape({
  id: PropTypes.number,
  slug: PropTypes.string,
  data: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
  }).isRequired,
  tags: PropTypes.arrayOf(PropTypes.number),
  votes: PropTypes.number,
  voted: PropTypes.bool,
});

QuestionList.propTypes = {
  questions: PropTypes.shape({
    pagination: PropTypes.object,
    results: PropTypes.arrayOf(QuestionShape),
  }).isRequired,
  isGated: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default QuestionList;
